// src/pages/VerRecurso.jsx
import { useMemo, useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft, ExternalLink, Download, Copy, FileText, Video as VideoIcon,
  Image as ImageIcon, RefreshCw, Maximize2, Minimize2, AlertTriangle
} from "lucide-react";

const safeDecode = (v) => { try { return v ? decodeURIComponent(v) : ""; } catch { return v || ""; } };
const getType = (u="") => {
  const x = u.toLowerCase();
  if (x.includes("youtube.com") || x.includes("youtu.be") || x.includes("vimeo.com")) return "link";
  if (/\.(pdf)(\?|#|$)/i.test(x)) return "pdf";
  if (/\.(mp4|webm|ogg)(\?|#|$)/i.test(x)) return "video";
  if (/\.(png|jpe?g|gif|bmp|webp|svg)(\?|#|$)/i.test(x)) return "image";
  return "link";
};

export default function VerRecurso() {
  const [sp] = useSearchParams();
  const navigate = useNavigate();

  const rawUrl = sp.get("url");
  const rawName = sp.get("nombre");
  const rawFrom = sp.get("from");      // <-- nuevo
  const rawCurso = sp.get("curso");    // <-- nuevo
  const rawModulo = sp.get("modulo");  // <-- nuevo

  const url    = useMemo(() => safeDecode(rawUrl || ""), [rawUrl]);
  const nombre = useMemo(() => safeDecode(rawName || "Recurso"), [rawName]);
  const from   = useMemo(() => safeDecode(rawFrom || ""), [rawFrom]);
  const curso  = useMemo(() => safeDecode(rawCurso || ""), [rawCurso]);
  const modulo = useMemo(() => safeDecode(rawModulo || ""), [rawModulo]);
  const type   = useMemo(() => getType(url), [url]);

  const [viewer, setViewer] = useState("auto");
  const [height, setHeight] = useState("80vh");
  const [loaded, setLoaded] = useState(false);
  const [hint, setHint] = useState(false);
  const [toast, setToast] = useState("");

  useEffect(() => {
    setLoaded(false);
    const t = setTimeout(() => setHint(true), 2500);
    return () => clearTimeout(t);
  }, [url, viewer, height]);

  if (!url) {
    return (
      <div className="vr-wrap">
        <div className="vr-state vr-err">
          <AlertTriangle size={18} />
          <span>Error: recurso no válido</span>
        </div>
        <style>{baseCss}</style>
      </div>
    );
  }

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(""), 1400); };

  const handleBack = () => {
    if (from) {
      navigate(from);
    } else if (curso && modulo) {
      navigate(`/curso/${curso}/modulo/${modulo}/recursos`);
    } else {
      navigate(-1);
    }
  };

  const openNewTab = () => window.open(url, "_blank", "noopener,noreferrer");
  const download = () => {
    const a = document.createElement("a");
    a.href = url; a.download = nombre || "recurso"; document.body.appendChild(a); a.click(); a.remove();
  };
  const copyUrl = async () => {
    try { await navigator.clipboard.writeText(url); showToast("Enlace copiado ✅"); }
    catch { showToast("No se pudo copiar 😕"); }
  };

  const PdfViewer = () => {
    const mode = viewer === "auto" ? "object" : viewer;
    return mode === "object" ? (
      <object data={url} type="application/pdf" className="vr-view" onLoad={() => setLoaded(true)}>
        <iframe src={url} title={nombre} className="vr-view" onLoad={() => setLoaded(true)} />
      </object>
    ) : (
      <iframe src={url} title={nombre} className="vr-view" onLoad={() => setLoaded(true)} />
    );
  };

  return (
    <div className="vr-wrap">
      <div className="vr-top">
        <button className="vr-back" onClick={handleBack}>
          <ArrowLeft size={18} /> Volver
        </button>

        <div className="vr-title">
          {type === "pdf" ? <FileText size={18} /> :
           type === "video" ? <VideoIcon size={18} /> :
           type === "image" ? <ImageIcon size={18} /> : <ExternalLink size={18} />}
          <strong className="vr-name" title={nombre}>{nombre}</strong>
        </div>

        <div className="vr-actions">
          {type === "pdf" && (
            <button className="vr-btn" onClick={() => setViewer(v => v === "iframe" ? "object" : "iframe")} title="Modo alterno PDF">
              <RefreshCw size={16} /> {viewer === "iframe" ? "Usar OBJECT" : "Usar IFRAME"}
            </button>
          )}
          <button className="vr-btn" onClick={openNewTab} title="Abrir en pestaña">
            <ExternalLink size={16} /> Abrir
          </button>
          <button className="vr-btn" onClick={download} title="Descargar">
            <Download size={16} /> Descargar
          </button>
          <button className="vr-btn" onClick={copyUrl} title="Copiar enlace">
            <Copy size={16} /> Copiar
          </button>

          <div className="vr-split" />

          <button
            className="vr-btn"
            onClick={() => setHeight(h => h === "80vh" ? "100vh" : "80vh")}
            title="Alternar alto"
          >
            {height === "100vh" ? <Minimize2 size={16} /> : <Maximize2 size={16} />} Alto {height}
          </button>
          <select className="vr-select" value={height} onChange={(e) => setHeight(e.target.value)}>
            <option value="60vh">60vh</option>
            <option value="80vh">80vh</option>
            <option value="100vh">100vh</option>
          </select>
        </div>
      </div>

      <div className="vr-frame" style={{ height }}>
        {!loaded && (
          <div className="vr-loading">
            <div className="vr-spinner" />
            <span>Cargando…</span>
          </div>
        )}

        {type === "pdf" && <PdfViewer />}
        {type === "video" && (
          <video
            className="vr-view vr-video"
            src={url}
            controls
            onLoadedData={() => setLoaded(true)}
            onError={() => setLoaded(true)}
          />
        )}
        {type === "image" && (
          <img src={url} alt={nombre} className="vr-view vr-img" onLoad={() => setLoaded(true)} onError={() => setLoaded(true)} />
        )}
        {type === "link" && (
          <iframe src={url} title={nombre} className="vr-view" onLoad={() => setLoaded(true)} />
        )}
      </div>

      {hint && !loaded && (
        <div className="vr-hint">
          <AlertTriangle size={16} />
          <span>
            Si ves la pantalla en blanco, el servidor podría bloquear la incrustación.
            Usa <strong>Abrir</strong> o cambia a <strong>Modo alterno PDF</strong>.
          </span>
        </div>
      )}

      {toast && <div className="vr-toast">{toast}</div>}

      <style>{baseCss}</style>
    </div>
  );
}

/* ===== CSS ===== */
const baseCss = `
:root{
  --radius: 14px;
  --border: #e5e7eb;
  --muted:#6b7280;
  --text:#111827;
  --brand:#0f5fb8;
  --brand-2:#1e3a8a;
  --bg:#ffffff;
  --soft:#f8fafc;
  --shadow: 0 10px 30px rgba(0,0,0,.08);
}

*{ box-sizing: border-box; }
.vr-wrap{ max-width: 1200px; margin: 0 auto; padding: 16px; color: var(--text); }

.vr-top{
  display:flex; align-items:center; gap:12px; flex-wrap:wrap;
  background: linear-gradient(135deg,#eef5ff,#ffffff);
  border:1px solid var(--border); border-radius: 14px; padding: 10px 12px;
  box-shadow: 0 6px 18px rgba(0,0,0,.04);
}

.vr-back{
  display:inline-flex; align-items:center; gap:6px; cursor:pointer;
  border:1px solid var(--border); background:#fff; color:#111827;
  padding:8px 10px; border-radius:10px; font-weight:700;
}
.vr-back:hover{ transform: translateY(-1px); box-shadow: 0 6px 16px rgba(0,0,0,.06); }

.vr-title{
  display:flex; align-items:center; gap:8px; min-width:200px; flex:1;
  color: var(--brand);
}
.vr-name{ max-width: 100%; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

.vr-actions{ display:flex; align-items:center; gap:8px; flex-wrap:wrap; }
.vr-btn{
  display:inline-flex; align-items:center; gap:6px; cursor:pointer;
  border:1px solid var(--border); background:#fff; color:#111827;
  padding:8px 10px; border-radius:10px; font-weight:800;
}
.vr-btn:hover{ transform: translateY(-1px); box-shadow: 0 6px 16px rgba(0,0,0,.06); border-color:#c7d2fe; }
.vr-select{
  padding:8px 10px; border:2px solid #e9ecef; border-radius:10px; outline:none; background:#fff; cursor:pointer;
}
.vr-select:focus{ border-color:#c7d2fe; }

.vr-split{ width:1px; height:24px; background:#e5e7eb; margin: 0 2px; }

.vr-frame{
  position:relative; margin-top:12px; border:1px solid var(--border); border-radius:14px; overflow:hidden;
  background: var(--soft);
  box-shadow: var(--shadow);
}
.vr-view{ width:100%; height:100%; border:0; display:block; }

.vr-video{ background:#000; }
.vr-img{ object-fit: contain; background:#111827; }

.vr-loading{
  position:absolute; inset:0; z-index:2; display:flex; flex-direction:column; gap:10px;
  align-items:center; justify-content:center; color:#374151; background: linear-gradient(180deg, rgba(255,255,255,.8), rgba(255,255,255,.5));
  backdrop-filter: blur(2px);
}
.vr-spinner{
  width:22px; height:22px; border:3px solid transparent; border-top-color:#2563eb; border-radius:999px;
  animation: vrspin 1s linear infinite;
}
@keyframes vrspin{ to{ transform: rotate(360deg);} }

.vr-hint{
  margin-top:10px; display:flex; align-items:center; gap:8px; color:#6b7280;
  background:#fff; border:1px dashed var(--border); border-radius:12px; padding:10px 12px;
}

.vr-state{
  display:flex; align-items:center; gap:8px; background:#fff; border:1px dashed var(--border);
  border-radius:12px; padding:14px; color:#6b7280;
}
.vr-err{ color:#b91c1c; border-color:#fecaca; background:#fff5f5; }

.vr-toast{
  position:fixed; bottom:16px; left:50%; transform: translateX(-50%);
  background:#111827; color:#fff; padding:10px 14px; border-radius:12px; box-shadow: 0 6px 24px rgba(0,0,0,.2);
  font-weight:700; z-index:9999;
}

@media (max-width: 760px){
  .vr-name{ max-width: 180px; }
}
`;
